import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { RegulifyService } from "../../api/RegulifyService";
import { useLoading } from "../../context/LoadingContext";
import { useExportTableToXLSX } from "../../hooks/useExportTableToXLSX";
import { useClinicalEvaluationStore } from "../../store/useClinicalEvaluationStore";
import { getSelectedStudyProductName } from "../../utils/clinicalStudy";
import { rememberClinicalExportSheet } from "../../utils/clinicalEvalExportTables";
import { extractPmid, normalizePmids } from "../../utils/clinicalEvalPipeline";
import Table from "../Table";
import {
  pmidColumn,
  relevanceColumn,
  searchTermColumn,
  titleColumn,
} from "./Columns";
import { toast } from "sonner";

const columnHelper = createColumnHelper();

const initialColumns = [
  columnHelper.accessor("title", titleColumn),
  columnHelper.accessor((row) => row.pmid, pmidColumn),
  columnHelper.accessor((row) => row.searchTerm, searchTermColumn),
  columnHelper.accessor((row) => row.relevance, relevanceColumn),
];

const Literatures = ({ goToNextTab }) => {
  const { loading, setLoading } = useLoading();
  const { selectedStudy, setPipelineStage } = useClinicalEvaluationStore(
    useShallow((state) => ({
      selectedStudy: state.selectedStudy,
      setPipelineStage: state.setPipelineStage,
    })),
  );
  const [columns, setColumns] = useState(initialColumns);
  const [data, setData] = useState();
  const [sorting, setSorting] = useState([]);
  const exportTableToXLSX = useExportTableToXLSX();
  const studyName = getSelectedStudyProductName(selectedStudy);

  const table = useReactTable({
    data,
    columns,
    enableColumnPinning: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: "includesString",
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    initialState: {
      columnPinning: {
        left: ["title", "pmid", "searchTerm"],
        right: [],
      },
    },
  });

  useEffect(() => {
    const getRelevantResults = async () => {
      try {
        if (!studyName) {
          toast.error("studyName is missing!");
          return;
        }

        setLoading(true, "Loading literature results… This may take a few minutes. Thank you for waiting.", "text-green-600");
        const response =
          await RegulifyService.cle_get_all_relevant_and_unique_results(
            studyName,
          );

        const combinedResults = response.data?.combined_results || [];

        //Find first result that actually has query_responses
        const firstNonEmptyData = combinedResults.find(
          (res) => Array.isArray(res.data) && res.data.length > 0,
        );

        const firstQueryResponses =
          firstNonEmptyData?.data?.[0]?.query_responses || [];

        //Build dynamic table columns safely
        const dynamicColumns = firstQueryResponses.map((col, index) =>
          columnHelper.accessor("c-" + index, {
            id: "c-" + index,
            cell: (info) => (
              <div
                className={`${index !== firstQueryResponses.length - 1 ? "text-center" : ""}`}
              >
                {info.getValue()}
              </div>
            ),
            header: () => (
              <span className="block w-full break-words whitespace-pre-line">
                {col.query}
              </span>
            ),
            size: index === firstQueryResponses.length - 1 ? 500 : 300,
            meta: {
              className: `${
                index === firstQueryResponses.length - 1
                  ? "max-w-[500px] min-w-[500px]"
                  : "max-w-[300px] min-w-[300px]"
              }`,
            },
          }),
        );

        //Prepare row data
        const updatedData = [];

        combinedResults.forEach((combinedResult) => {
          const relevancyResults = combinedResult.data || [];
          const resultData = relevancyResults.map((item) => {
            const dataItem = {
              title: item.title,
              pmid: item.pmid,
              relevance: item.relevancy === "no" ? "Not relevant" : "Relevant",
              searchTerm: combinedResult.search_term,
            };

            (item.query_responses || []).forEach(
              (res, index) => (dataItem["c-" + index] = res.response),
            );

            return dataItem;
          });

          updatedData.push(...resultData);
        });

        const currentPmids = normalizePmids(
          updatedData.map((item) => extractPmid(item)),
        );
        setPipelineStage("relevancy", currentPmids, studyName);

        const exportColumns = [...initialColumns, ...dynamicColumns];
        rememberClinicalExportSheet(studyName, "literatures", {
          data: updatedData,
          columns: exportColumns,
        });
        setColumns(exportColumns);
        setData(updatedData);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };

    getRelevantResults();
  }, []);

  return (
    <div className="flex h-full flex-col">
      <div className="h-[calc(100vh-180px)] px-4">
        {data && data.length > 0 && (
          <div className="flex items-end justify-center gap-2">
            <div className="mt-4 flex w-full justify-end gap-2">
              <input
                value={table.getState().globalFilter}
                className="input input-bordered w-full"
                onChange={(e) => table.setGlobalFilter(String(e.target.value))}
                placeholder="Search..."
              />
              <button
                className="btn tooltip tooltip-left"
                data-tip="Download the below data in excel format"
                onClick={() => exportTableToXLSX(data, columns)}
              >
                export
              </button>
              <button
                className="btn btn-primary tooltip tooltip-left"
                data-tip="Click to proceed"
                onClick={goToNextTab}
                disabled={loading}
              >
                Next
              </button>
            </div>
          </div>
        )}
        {data && (
          <div className="max-h-[calc(100vh-280px)] overflow-y-scroll">
            <Table table={table} className="text-black" />
          </div>
        )}
      </div>
    </div>
  );
};

export default Literatures;
